import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BookOpen, Clock, CheckCircle2, Circle, PlayCircle, ArrowLeft, Award } from "lucide-react";
import Seo, { SITE, breadcrumbSchema } from "../components/Seo";
import { api } from "../lib/api";
import { useLearning, courseStats, LEVEL_STYLES } from "../lib/learning";
import NotFound from "./NotFound";

export default function CoursePage() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [missing, setMissing] = useState(false);
  const { user, progress, certificates, loaded } = useLearning();
  useEffect(() => { api.get(`/courses/${slug}`).then((r) => setCourse(r.data)).catch(() => setMissing(true)); }, [slug]);

  if (missing) return <NotFound />;
  if (!course) return <div className="min-h-[60vh] bg-navy" data-testid="course-loading" />;
  const s = courseStats(course, progress[slug]);
  const schema = [{
    "@context": "https://schema.org", "@type": "Course", name: course.title, description: course.summary,
    url: `${SITE}/learn/${slug}`, educationalLevel: course.level, isAccessibleForFree: true,
    provider: { "@type": "Organization", name: "Quantora", sameAs: SITE },
    hasCourseInstance: { "@type": "CourseInstance", courseMode: "online", courseWorkload: `PT${course.total_minutes}M` },
  }, breadcrumbSchema([["Home", "/"], ["Learn", "/learn"], [course.title, `/learn/${slug}`]])];

  return (
    <>
      <Seo title={`${course.title} – ${course.level} Course`} description={course.summary} path={`/learn/${slug}`} schema={schema} image={course.cover_image} />
      <section className="grain relative overflow-hidden bg-navy pb-20 pt-14 text-white">
        <div className="container-q relative grid gap-12 lg:grid-cols-[1.2fr_1fr] lg:items-center">
          <div>
            <Link to="/learn" data-testid="course-back-link" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft size={15} /> All courses</Link>
            <div className="mt-6 flex items-center gap-3">
              <span className={`rounded-full px-3 py-1 text-xs font-bold ${LEVEL_STYLES[course.level]}`}>{course.level}</span>
              <span className="text-xs uppercase tracking-wider text-gold">{course.category}</span>
            </div>
            <h1 className="mt-4 text-4xl font-bold sm:text-5xl" data-testid="course-title">{course.title}</h1>
            <p className="mt-5 max-w-xl text-slate-300">{course.description}</p>
            <div className="mt-6 flex gap-6 text-sm text-slate-400">
              <span className="inline-flex items-center gap-2"><BookOpen size={16} /> {course.lesson_count} lessons</span>
              <span className="inline-flex items-center gap-2"><Clock size={16} /> {course.total_minutes} minutes</span>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to={s.nextPath} data-testid="course-start-btn" aria-busy={!loaded} className={`inline-flex items-center gap-2 rounded-full bg-mint px-6 py-3.5 text-sm font-semibold text-navy transition-[transform,opacity] hover:-translate-y-0.5 ${loaded ? "opacity-100" : "pointer-events-none opacity-0"}`}>
                <PlayCircle size={17} /> {s.completed ? "Review course" : s.quizPending ? "Take final quiz" : s.started ? "Continue learning" : "Start course"}
              </Link>
              {!user && <Link to="/login" data-testid="course-login-link" className="text-sm text-slate-300 underline-offset-4 hover:underline">Sign in to track progress</Link>}
            </div>
            {user && progress[slug] && (
              <div className="mt-8 max-w-md">
                <div className="h-2 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-mint" style={{ width: `${s.pct}%` }} /></div>
                <p className="mt-2 text-xs text-slate-400" data-testid="course-progress-text">{s.done.length} of {s.total} lessons complete{s.hasQuiz ? ` · quiz ${s.quizPassed ? "passed" : "pending"}` : ""} · {s.pct}%</p>
              </div>
            )}
            {certificates[slug] && (
              <Link to={`/certificates/${certificates[slug].id}`} data-testid="course-certificate-btn" className="mt-6 inline-flex items-center gap-2 rounded-full border border-gold/60 px-5 py-2.5 text-sm font-semibold text-gold hover:bg-gold/10">
                <Award size={16} /> View your certificate
              </Link>
            )}
          </div>
          {course.cover_image && <img src={course.cover_image} alt={course.title} className="aspect-[16/11] w-full rounded-3xl border border-white/10 object-cover shadow-2xl" />}
        </div>
      </section>
      <section className="bg-slate-100/70 py-20">
        <div className="container-q grid gap-10 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <h2 className="text-2xl font-bold text-navy">Curriculum</h2>
            <ol className="mt-6 space-y-3" data-testid="course-curriculum">
              {course.lessons.map((l, i) => {
                const done = s.done.includes(l.id);
                return (
                  <li key={l.id}>
                    <Link to={`/learn/${slug}/${l.id}`} data-testid={`curriculum-lesson-${l.id}`} className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-shadow hover:shadow-lg">
                      {done ? <CheckCircle2 size={20} className="shrink-0 text-mint" /> : <Circle size={20} className="shrink-0 text-slate-300" />}
                      <span className="font-num text-xs text-slate-400">{String(i + 1).padStart(2, "0")}</span>
                      <span className="flex-1 font-semibold text-navy group-hover:text-mint-600">{l.title}</span>
                      <span className="text-xs text-slate-500">{l.duration_minutes} min</span>
                    </Link>
                  </li>
                );
              })}
              {s.hasQuiz && (
                <li>
                  <Link to={`/learn/${slug}/quiz`} data-testid="curriculum-quiz" className="group flex items-center gap-4 rounded-2xl border border-gold/50 bg-gold-50 p-5 transition-shadow hover:shadow-lg">
                    {s.quizPassed ? <CheckCircle2 size={20} className="shrink-0 text-mint" /> : <Award size={20} className="shrink-0 text-gold-600" />}
                    <span className="flex-1 font-semibold text-navy">Final quiz · earn your certificate</span>
                    <span className="text-xs text-slate-500">{course.quiz_question_count} questions · pass {course.quiz?.pass_pct}%</span>
                  </Link>
                </li>
              )}
            </ol>
          </div>
          <aside className="h-fit rounded-3xl border border-slate-200 bg-white p-8">
            <h3 className="font-bold text-navy">What you will learn</h3>
            <ul className="mt-5 space-y-3">
              {course.lessons.flatMap((l) => l.takeaways.slice(0, 1)).map((t) => <li key={t} className="flex gap-3 text-sm text-slate-600"><CheckCircle2 size={16} className="mt-0.5 shrink-0 text-mint" /> {t}</li>)}
            </ul>
            <p className="mt-8 border-t border-slate-100 pt-5 text-xs text-slate-500">Educational content only. Not investment, insurance or tax advice.</p>
          </aside>
        </div>
      </section>
    </>
  );
}
